import { CatalogueItem } from 'src/features/common';
import { OnboardingDocumentType } from './constants';
import { DeliveryDetails } from 'src/features/order/types/delivery-details';
import { CardMachine } from 'src/features/common/types/application/card-machine';
import { Offer, RequestOffer } from 'src/context/offer-context';

export const intersection = (arr1: [], arr2: []) => {
  const set1 = new Set(arr1);
  // @ts-ignore
  return [...new Set<[]>(arr2)].filter((val) => set1.has(val));
};

/**
 * Function that performs a partial deep comparison between a given object and comparitor,
 * returning true if the given object has equivalent property values, else false.
 * See tests for examples
 * @param {objectA} obj Object used for comparison
 * @param {source} obj Check object where all key value pairs must exist in objectA.
 */

export const matchesKVP = (objectA, comparitor) =>
  Object.entries(comparitor).every((kvPair) => objectA[kvPair[0]] === kvPair[1]);

export function deepEqual(valueA: any, valueB: any) {
  if (!!valueA && !!valueB && typeof valueA === 'object' && typeof valueB === 'object') {
    const valueAKeys = Object.keys(valueA);
    const valueBKeys = Object.keys(valueB);

    return (
      valueAKeys.length === valueBKeys.length &&
      valueAKeys.every((key) => deepEqual(valueA[key], valueB[key]))
    );
  }

  return valueA === valueB;
}

export function ordinalSuffixOf(number: number) {
  const tenthModule = number % 10;
  const hundredthModule = number % 100;
  if (tenthModule === 1 && hundredthModule !== 11) {
    return number + 'st';
  }
  if (tenthModule === 2 && hundredthModule !== 12) {
    return number + 'nd';
  }
  if (tenthModule === 3 && hundredthModule !== 13) {
    return number + 'rd';
  }
  return number + 'th';
}

export const getDocumentTypeCaseInsensitive = (documentTypeAnyCase: string) => {
  const documentTypeLowerCase = documentTypeAnyCase.toLowerCase();
  const documentTypes = Object.values(OnboardingDocumentType);
  return (
    documentTypes.find((documentType) => documentType.toLowerCase() === documentTypeLowerCase) ||
    documentTypeAnyCase
  );
};

export function parseObjectToQueryParamString(object: object) {
  return Object.keys(object)
    .map((key) => `${key}=${object[key]}`)
    .join('&');
}

// converts any text into a format that is suitable to be used as or in an automation id
export function convertToAutomationId(text: string) {
  return text
    .replace(/[^a-zA-Z0-9]/g, ' ') // replace any chracters that are not alphanumeric with a space
    .split(' ') // splits into multiple strings on space character
    .map((textChunk) => textChunk.trim())
    .filter((textChunk) => textChunk.length > 0)
    .map((textChunk) => textChunk[0].toUpperCase() + textChunk.substr(1).toLowerCase()) // applies title case
    .join('_'); // concats all strings using underscore character as delimiter
}

export const getImageSizeFromBase64 = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const binaryString = atob(base64);
  const fileSizeInBytes = binaryString.length;

  return fileSizeInBytes;
};

export const isDeviceRented = (products: CatalogueItem[] = []): boolean =>
  !!products.find(({ device, price }) => price?.priceType === 'MONTHLY' && device?.quantity > 0);

export const isDeviceScanned = (
  deliveryDetails: DeliveryDetails,
  cardMachines: CardMachine[],
): boolean => {
  return !!(
    deliveryDetails.customAddresses?.some((addressItem) => {
      return addressItem.solutions.some((solution) => {
        return (
          solution.productId === cardMachines?.[0]?.productId &&
          solution.acquiringChannel === cardMachines?.[0]?.acquiringChannel
        );
      });
    }) ||
    deliveryDetails.defaultAddress?.solutions?.some((defaultItem) => {
      return (
        defaultItem.productId === cardMachines?.[0]?.productId &&
        defaultItem.acquiringChannel === cardMachines?.[0]?.acquiringChannel
      );
    })
  );
};

export const getAvailableLimit = (
  amountTermsMap: Record<string, number[]>,
  offerDetails: RequestOffer['offerDetails'],
): number => {
  if (offerDetails.AvailableLimit) {
    return offerDetails.AvailableLimit;
  }

  const amounts = Object.keys(amountTermsMap).map(Number);

  if (amounts.length === 0) {
    return 0; // No amounts in the map
  }

  return Math.max(...amounts);
};

export function capitalizeKeysAndAddRate(obj: Offer): Offer {
  const result: Offer = { ...obj };

  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);
      result[capitalizedKey] = result[key];

      if (key === 'id') {
        result['OfferId'] = result[key];
      }

      if (key === 'dailyAffordability' && result[key]) {
        result['DailyAffordability'] = result[key].toFixed(2);
      }

      // Add a new property 'Rate' based on the value of 'Remittance'
      if (key === 'remittance') {
        result['Rate'] = (parseFloat(result[key]) * 100).toFixed(2).toString();
      }
    }
  }

  return result;
}

export function filterObjectByKeys<T extends Record<string, unknown>>(
  originalObject: T,
  keysToFilter: (keyof T)[],
) {
  return Object.fromEntries(
    Object.entries(originalObject).filter(([key]) => keysToFilter.includes(key as keyof T)),
  ) as Partial<T>;
}

export function findFirstDuplicateIndex<T>(arr: T[]): number {
  const seen = new Set<T>();
  for (let i = 0; i < arr.length; i++) {
    if (seen.has(arr[i])) {
      return i;
    }
    seen.add(arr[i]);
  }
  return -1;
}
